/**
 * This file holds IWV's definitions of languages and locales, as well as various helper methods to support
 * how locales are used in IWV.
 *
 * As a baseline:
 * - 'language' refers to the English name of a given language
 * - 'locale' refers to the ISO code for a given language
 *
 * How locales are defined in IWV:
 * - the 'default' locales configured as English (en), Spanish (es)
 * - the 'custom' locales configured as any/all locales a US State can opt into supporting
 * - and to unify the two, 'active' locales
 *
 * Why differentiate between default and custom locales?
 * In IWV's source code:
 * - All localized strings are expected to be translated across all locales (default AND custom)
 * HOWEVER, in Contentful:
 * - At _minimum_, English and Spanish content is expected to exist, because IWV supports
 *   English and Spanish content across the board nationally
 * - Any/all other localized content is not necessarily guaranteed to exist,
 *   because each US State + DC has to "opt in" to support a given locale.
 *
 * It is not unreasonable to expect that the definition of "default" locales
 * could expand beyond English and Spanish. However, given the unique population
 * makeup of each US State + DC, even if the definition of "default" locales expands,
 * there will still be a need for customizable/opt-in locale support on a state-by-state basis.
 *
 * Given all of the above, both IWV and Contentful need to have definitions for _every_ locale
 * supported across all states, no matter how many US States support each locale.
 *
 * The `languageSupport` field, configured on the Contentful Jurisdiction Config object, describes which languages,
 * a US State/DC is opting into support. The `languageSupport` field is configured as an array of language names,
 * for ease of human legibility.
 *
 * In order to support the expected locales for a given US State/DC, IWV maps the `languageSupport` field values
 * to each _language_'s corresponding _locale_. IWV is oriented to operate locale-first, instead of language-name-first,
 * for ease of code execution.
 */
import { BLOCKS } from '@contentful/rich-text-types';

import { Option } from './option';

export const DefaultLanguageToLocale = {
  English: 'en',
  Spanish: 'es',
} as const;
export type DefaultLanguage = keyof typeof DefaultLanguageToLocale;
export type DefaultLocale = (typeof DefaultLanguageToLocale)[DefaultLanguage];

/**
 * CustomLanguageToLocale represents the Contentful
 * configuration of language names to ISO locale codes.
 *
 * NOTE: When updating locales and the `languageSupport`
 * field in Contentful, {@link CustomLanguageToLocale}
 * needs to be updated as well.
 */
export const CustomLanguageToLocale = {
  Arabic: 'ar',
  Shoshone: 'shh',
  'Simplified Chinese': 'zh-Hans',
  Tagalog: 'tl',
} as const;
export type CustomLanguage = keyof typeof CustomLanguageToLocale;
export type CustomLocale = (typeof CustomLanguageToLocale)[CustomLanguage];
export const ActiveLanguageToLocale = {
  ...DefaultLanguageToLocale,
  ...CustomLanguageToLocale,
};

/**
 * Invert mapping of ActiveLanguageToLocale
 * so language name values can be accessed with locale keys
 * e.g. LocaleToLanguage["es"] = "Spanish"
 */
export const ActiveLocaleToLanguage = Object.fromEntries(
  Object.entries(ActiveLanguageToLocale).map(([lang, locale]) => [locale, lang])
);

export type ActiveLanguage = DefaultLanguage | CustomLanguage;
export type ActiveLocale = DefaultLocale | CustomLocale;

export const DEFAULT_LOCALES: ActiveLocale[] = [
  ActiveLanguageToLocale['English'],
  ActiveLanguageToLocale['Spanish'],
];

/**
 * Expected use cases for isDefaultLocale:
 * - temporary validation for toggling a locale before
 * language selector exists
 */
export function isDefaultLocale(locale: string): locale is DefaultLocale {
  const defaultLocales = Object.values(DefaultLanguageToLocale);
  return defaultLocales.includes(locale as DefaultLocale);
}

/**
 * RIGHT_TO_LEFT_LOCALES defines locales which are
 * read right to left, instead of left to right.
 * When {@link CustomLanguageToLocale} support is
 * expanded, engineers need to be sensitive to update
 * RIGHT_TO_LEFT_LOCALES as needed/when appropriate.
 */
export const RIGHT_TO_LEFT_LOCALES: ActiveLocale[] = [
  ActiveLanguageToLocale['Arabic'],
];

export function localeReadsRightToLeft(locale: ActiveLocale) {
  return RIGHT_TO_LEFT_LOCALES.includes(locale);
}

/**
 * Expected use cases for isActiveLocale:
 * - validating whether IWV has a definition for a locale
 * that comes from query params
 * - support to validate whether IWV has a definition for
 * a user's preferred browser language (represented as a locale)
 */
export function isActiveLocale(locale: string): locale is ActiveLocale {
  const activeLocales = Object.values(ActiveLanguageToLocale);
  return activeLocales.includes(locale as ActiveLocale);
}

/**
 * Expected use cases for isActiveLanguage:
 * - validating whether IWV has a definition for a language
 * that comes from Contentful
 * This will specifically protect production from attempting
 * to handle a newly-added-in-Contentful language, before
 * IWV has been updated to support said language
 * and its corresponding locale.
 */
export function isActiveLanguage(lang: string): lang is ActiveLanguage {
  const activeLanguages = Object.keys(ActiveLanguageToLocale).map(
    (langKey) => langKey as ActiveLanguage
  );
  return activeLanguages.includes(lang as ActiveLanguage);
}

// LANGUAGE SELECTOR LABELS
/**
 * Language selector labels used to articulate
 * 'in <LANGUAGE NAME>' in the target language.
 */
export const ActiveLocaleToInLanguage: {
  [key in ActiveLocale]: string;
} = {
  en: 'In English',
  es: 'en Español',
  ar: 'باللغة العربية',
  tl: 'sa Tagalog',
  shh: 'Shoshone',
  'zh-Hans': '中文（简体）',
};

/**
 * The word "more" (as in "additional")
 * used to label the `<AdditionalLocaleSelectors/>`
 * toggle button which expands to show more languages
 * on mobile.
 */
export const MoreLocalizedToActiveLocales: {
  [key in ActiveLocale]: string;
} = {
  en: 'More',
  es: 'Más',
  ar: 'المزيد من اللغات',
  tl: 'Karagdagang',
  shh: 'More',
  'zh-Hans': '更多的',
};

/**
 * The phrase 'Site Language' used as an aria-label
 * for the `<SiteLanguageMenu/>`.
 */
export const SiteLangLocalizedToActiveLocales: {
  [key in ActiveLocale]: string;
} = {
  en: 'Site Language',
  es: 'Idioma del sitio',
  ar: 'لغة الموقع',
  tl: 'Lenggwahe ng Site',
  shh: 'Site Language',
  'zh-Hans': '网站语言',
};

// STRING TYPES

/**
 * Localized string content expected to exist
 * in the {@link DefaultLocale}s: 'en' and 'es'.
 */
export type DefaultLocalizedString = {
  [key in DefaultLocale]: string;
};

/**
 * Localized string content that may be null,
 * in the {@link DefaultLocale}s: 'en' and 'es'.
 * VepApi DefaultLocale content is safely nullable
 * where the content may not exist
 * ex: custom state voter hotline
 */
export type VepApiDefaultLocalizedNullableString = {
  [key in DefaultLocale]: string | null;
};

/**
 * Localized string content that may or may
 * not exist, localized to {@link CustomLocale}s.
 */
export type CustomLocalizedString = {
  [key in CustomLocale]?: string;
};

/**
 * Custom locales are optional for both
 * {@link CustomLocalizedString}
 * and VepApiCustomLocalizedNullableString.
 *
 * The localized content is a string or null
 * for VepApiCustomLocalizedNullableString.
 */
export type VepApiCustomLocalizedNullableString = {
  [key in CustomLocale]?: string | null;
};

/**
 * Unifies expected default language content (en/es)
 * with variable locale content (see {@link CustomLocale}).
 */
export type ActiveLocalizedString = DefaultLocalizedString &
  CustomLocalizedString;

/**
 * VepApi content is safely nullable across locales
 * Unifies expected/nullable default language content (en/es)
 * with variable and nullable locale content
 * (see {@link CustomLocale})
 */
export type VepApiActiveLocalizedNullableString =
  VepApiDefaultLocalizedNullableString & VepApiCustomLocalizedNullableString;

// URL TYPES

// BEGIN SINGLE URL PER LOCALE
/**
 * Localized url expected to exist
 * in the {@link DefaultLocale}s: 'en' and 'es'.
 */
export type DefaultLocalizedUrl = {
  [key in DefaultLocale]: string;
};

/**
 * Localized url that may or may
 * not exist, localized to {@link CustomLocale}s.
 */
export type CustomLocalizedUrl = {
  [key in CustomLocale]?: string;
};

/**
 * Unifies expected default locale urls (en/es)
 * with variable language urls (see {@link CustomLocale})
 */
export type ActiveLocalizedUrl = DefaultLocalizedUrl & CustomLocalizedUrl;
// END SINGLE URL PER LOCALE

// BEGIN MULTIPLE URLS PER LOCALE
/**
 * Localized url arrays expected to exist
 * in the {@link DefaultLocale}s: 'en' and 'es'.
 */
export type DefaultLocalizedUrlArray = {
  [key in DefaultLocale]: string[];
};

/**
 * Localized url arrays that may or may
 * not exist, localized to {@link CustomLocale}s.
 */
export type CustomLocalizedUrlArray = {
  [key in CustomLocale]?: string[];
};

/**
 * Unifies expected default language url arrays (en/es)
 * with variable locale url arrays (see {@link CustomLocale})
 */
export type ActiveLocalizedUrlArray = DefaultLocalizedUrlArray &
  CustomLocalizedUrlArray;
// END MULTPLE URLS PER LOCALE

// BEGIN LOCALIZED BOOLEAN TYPES
/**
 * Localized boolean expected to exist
 * in the {@link DefaultLocale}s: 'en' and 'es'.
 */
export type DefaultLocalizedBoolean = {
  [key in DefaultLocale]: boolean;
};

/**
 * Localized boolean that may or may
 * not exist, localized to {@link CustomLocale}s.
 */
export type CustomLocalizedBoolean = {
  [key in CustomLocale]?: boolean;
};

/**
 * Unifies expected default booleans (en/es)
 * with variable locale booleans (see {@link CustomLocale})
 */
export type ActiveLocalizedBoolean = DefaultLocalizedBoolean &
  CustomLocalizedBoolean;
// END LOCALIZED BOOLEAN TYPES

// BEGIN LOCALIZED RICH TEXT TYPES
export type RichTextObject = {
  /* The richtext json object from Contentful
  is very bloated. The basic/guaranteed attributes
  are nodeType, data, and content.
    In testing, data seemed to remain an empty object.
    Content contains all of the richtext data parsed by
  the documentToReactComponent() method from react-rich-text-renderer.
  See an example of this object here:
  https://www.contentful.com/developers/docs/concepts/rich-text/
  */
  nodeType: BLOCKS.DOCUMENT;
  data: {};
  /** This is all Contentful internal stuff so we don’t care about the type. */
  content: Array<any>;
};

/**
 * Localized rich text content expected to exist
 * in the {@link DefaultLocale}s: 'en' and 'es'.
 */
export type DefaultLocalizedRichTextObj = {
  [key in DefaultLocale]: RichTextObject;
};

/**
 * Localized rich text content that may or may
 * not exist, localized to {@link CustomLocale}s.
 */
export type CustomLocalizedRichTextObj = {
  [key in CustomLocale]?: RichTextObject;
};

/**
 * Unifies expected default rich text content (en/es)
 * with variable locale rich text content (see {@link CustomLocale})
 */
export type ActiveLocalizedRichTextObj = DefaultLocalizedRichTextObj &
  CustomLocalizedRichTextObj;

// END LOCALIZED RICH TEXT TYPES

// BEGIN HELPER METHODS FOR LOCALIZED CONTENT

/**
 * Reusable helper function that retrieves either:
 * - the localized content for a requested locale, if the content exists,
 * or
 * - the localized content in English/Spanish, based on the hostname
 *
 * USECASE: While localized content in IWV's source code should be localized across
 * all locales, Contentful objects are not required to have content for all
 * locales -- only the locales a US State supports translations for, described in
 * {@link JurisdictionLocaleSupport}.
 * @param locale An {@link ActiveLocale}
 * @param content Either an {@link ActiveLocalizedString} or {@link ActiveLocalizedUrl}, which
 * may or may not have content available for the requested locale.
 * @param fallbackLocale 'en' (English) or 'es' (Spanish), as determined by hostname.
 */
export function getLocalizedContent(
  locale: ActiveLocale,
  content:
    | ActiveLocalizedString
    | ActiveLocalizedUrl
    | VepApiActiveLocalizedNullableString,
  fallbackLocale: DefaultLocale = defaultHostLocale()
): string {
  let fallbackContent = content[fallbackLocale] ?? '';
  // if the fallback locale is Spanish, but there is no content in Spanish, fallback to English content
  if (fallbackLocale === 'es' && !content['es'] && content['en']) {
    fallbackContent = content['en'];
  }
  return content[locale] ?? fallbackContent;
}

/**
 * Reusable helper function (specific to RichText from Contentful) that retrieves either:
 * - the localized rich text content for a requested locale, if the content exists,
 * or
 * - the localized rich text content in English/Spanish, based on the hostname
 *
 * USECASE: Contentful rich text content may not exist for all locales in all jurisdictions.
 * {@link JurisdictionLocaleSupport} describes the locales a jurisdiction supports.
 * @param locale An {@link ActiveLocale}
 * @param richTextObject {@link ActiveLocalizedRichText} object which may/may not have content available for the requested locale.
 * @param fallbackLocale 'en' (English) or 'es' (Spanish), as determined by hostname.
 */
export function getLocalizedRichText(
  locale: ActiveLocale,
  richTextObject: ActiveLocalizedRichTextObj,
  fallbackLocale: DefaultLocale = defaultHostLocale()
): RichTextObject {
  return richTextObject[locale] ?? richTextObject[fallbackLocale];
}

// END HELPER METHODS FOR LOCALIZED CONTENT

// BEGIN URL METHODS FOR HANDLING LOCALIZATION
/**
 * Returns the locale based on the host name. So “voyavotar.com” would return
 * {@link DefaultLanguageToLocale[Spanish]} ('es') while “iwillvote.com” (and any other host, it’s the
 * default) returns {@link DefaultLanguageToLocale[English]} ('en').
 */
export function defaultHostLocale(
  location: Pick<Location, 'host'> = window.location
): DefaultLocale {
  const esPattern = RegExp(/voyavotar/);

  if (location && esPattern.exec(location.host)) {
    return DefaultLanguageToLocale['Spanish'];
  } else {
    return DefaultLanguageToLocale['English'];
  }
}

/**
 * Returns a locale if specified by user browser preference.
 * Handles a user's preference with awareness of ISO codes
 * with country codes (which may not be _exact_ matches to an
 * {@link ActiveLocale}).
 */
export function defaultBrowserLocale(
  userPreference: Pick<Navigator, 'language'>
): Option<ActiveLocale> {
  // handle ISO locales with country code
  const preferredLocale = userPreference.language.split('-')[0]!;
  if (isActiveLocale(preferredLocale)) {
    return preferredLocale;
  } else {
    return undefined;
  }
}

// END URL METHODS FOR HANDLING LOCALIZATION
